/* Owns overlay focus behaviour shared by Modal, Drawer and the command palette:
   move focus in, keep Tab inside, close the TOPMOST overlay on Escape, and put
   focus back on the trigger when the overlay closes. */

import { useEffect, useRef } from 'react'

const FOCUSABLE =
  'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])'

/** Overlays register here in mount order so Escape only closes the last one opened. */
const overlayStack: symbol[] = []

export interface FocusTrapOptions {
  open: boolean
  onClose: () => void
  /** Set false for an overlay that must not close on Escape (a required confirmation). */
  closeOnEscape?: boolean
}

export function useFocusTrap<T extends HTMLElement>(options: FocusTrapOptions): React.RefObject<T | null> {
  const { open, onClose, closeOnEscape = true } = options
  const containerRef = useRef<T | null>(null)
  const previouslyFocused = useRef<HTMLElement | null>(null)
  const idRef = useRef<symbol>(Symbol('overlay'))

  useEffect(() => {
    if (!open) return
    const id = idRef.current
    overlayStack.push(id)
    previouslyFocused.current = document.activeElement as HTMLElement | null

    const container = containerRef.current
    const first = container?.querySelector<HTMLElement>(FOCUSABLE)
    ;(first ?? container)?.focus()

    const onKeyDown = (event: KeyboardEvent): void => {
      if (event.key === 'Escape' && closeOnEscape) {
        // Only the overlay on top reacts, so a confirm dialog opened over a
        // drawer does not close both with one press.
        if (overlayStack[overlayStack.length - 1] !== id) return
        event.stopPropagation()
        onClose()
        return
      }
      if (event.key !== 'Tab') return
      const node = containerRef.current
      if (!node) return
      const focusable = [...node.querySelectorAll<HTMLElement>(FOCUSABLE)].filter(
        (element) => element.offsetParent !== null || element === document.activeElement,
      )
      if (focusable.length === 0) return
      const firstElement = focusable[0]
      const lastElement = focusable[focusable.length - 1]
      if (!firstElement || !lastElement) return
      if (event.shiftKey && document.activeElement === firstElement) {
        event.preventDefault()
        lastElement.focus()
      } else if (!event.shiftKey && document.activeElement === lastElement) {
        event.preventDefault()
        firstElement.focus()
      }
    }

    document.addEventListener('keydown', onKeyDown, true)
    return () => {
      document.removeEventListener('keydown', onKeyDown, true)
      const index = overlayStack.indexOf(id)
      if (index >= 0) overlayStack.splice(index, 1)
      previouslyFocused.current?.focus?.()
    }
  }, [open, onClose, closeOnEscape])

  return containerRef
}
